import { PlusRegularIcon } from "@elixir/icons";
import { usePackageBookingStore } from "@elixir/stores";
import { HealthPackageItem, PACKAGE_POPUP_TYPE } from "@elixir/types";
import { useVirtualizer } from "@tanstack/react-virtual";
import React from "react";
import {
  ManageTreatmentsTableRowItemUi,
  PrimaryButton,
  TableHeaderUi,
  getSettingsBodyConfig,
} from "ui";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";
import useUpdateAPackage from "../../../apis/tryst/packages-and-treatments/use-update-a-package";
import { track } from "../../../utils/tracking";

const headerConfig: Array<{
  key: string;
  width?: string | number;
  title: string;
  align?: "left" | "right" | "center";
}> = [
  {
    key: "package_name",
    title: "Package Name",
    width: "30%",
  },
  {
    key: "sessions",
    title: "Sessions",
    width: "20%",
  },
  {
    key: "price",
    title: "Price",
    width: "20%",
  },
  {
    key: "archive",
    title: "Inactive/active",
    width: "20%",
    align: "right",
  },
  {
    key: "edit",
    title: "Actions",
    width: "10%",
    align: "right",
  },
];

const bodyConfig = getSettingsBodyConfig(headerConfig);

const ManageTreatmentPackages = () => {
  const setPackageDetails = usePackageBookingStore(
    (state) => state.setPackageDetails
  );
  const setPackagePopupType = usePackageBookingStore(
    (state) => state.setPackagePopupType
  );

  const { data: queueConfig, isLoading: isQueueConfigLoading } =
    useGetQueueConfig();
  const queueConfigPackages = queueConfig?.data?.packages || [];

  const { mutateAsync: updatePackage } = useUpdateAPackage();

  const parentRef = React.useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: queueConfigPackages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
  });

  const items = rowVirtualizer.getVirtualItems();

  const handleCreateNewPackage = () => {
    track("handle_create_new_package_click");
    setPackagePopupType(PACKAGE_POPUP_TYPE.CREATE_PACKAGE);
  };

  const handleEditPackage = (packageItem: HealthPackageItem) => {
    track("handle_edit_package_click");
    setPackageDetails(packageItem);
    setPackagePopupType(PACKAGE_POPUP_TYPE.EDIT_PACKAGE);
  };

  const handleSwitchChange = async (packageItem: HealthPackageItem) => {
    track("handle_switch_change_click");
    await updatePackage({
      data: { ...packageItem, active: !packageItem.active },
    });
  };

  return (
    <div className="dr-bg-darwin-touch-neutral-0 dr-py-12 dr-px-16 dr-space-y-16 dr-w-full dr-h-full">
      <div className="dr-flex dr-items-center dr-justify-between">
        <div className="HeadlineBold dr-text-darwin-touch-neutral-1000">
          Treatment packages
        </div>
        <PrimaryButton
          buttonIconPosition="left"
          title="Create new"
          onClick={handleCreateNewPackage}
          IconComponent={() => <PlusRegularIcon className="dr-w-16 dr-h-16" />}
        />
      </div>
      <div className="dr-flex dr-flex-col dr-w-full dr-border dr-border-darwin-touch-neutral-200 dr-rounded-8 dr-overflow-hidden dr-h-[calc(100%-76px)]">
        <TableHeaderUi headerConfig={headerConfig} />
        {!isQueueConfigLoading && queueConfigPackages.length === 0 ? (
          <div className="dr-flex-1 dr-flex dr-justify-center dr-items-center dr-flex-col dr-space-y-8">
            <div className="SubheadlineRegular">Please create a package</div>
            <PrimaryButton
              title="Create Package"
              onClick={handleCreateNewPackage}
            />
          </div>
        ) : (
          <div className="dr-flex-1 dr-w-full dr-overflow-y-hidden">
            <div
              ref={parentRef}
              style={{
                height: "100%",
                overflowY: "auto",
                contain: "strict",
                scrollbarWidth: "none",
              }}
            >
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${items[0]?.start ?? 0}px)`,
                  }}
                >
                  {items.map((virtualItem) => {
                    const isLastItem =
                      virtualItem.index === queueConfigPackages.length - 1;
                    const currRow = queueConfigPackages[virtualItem.index];

                    return (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={rowVirtualizer.measureElement}
                      >
                        <ManageTreatmentsTableRowItemUi
                          key={currRow?.package_id}
                          rowConfig={bodyConfig}
                          packageData={currRow}
                          isLastItem={isLastItem}
                          onEditClick={() => handleEditPackage(currRow)}
                          onSwitchClick={() => handleSwitchChange(currRow)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageTreatmentPackages;
